import { ReactNode, useState } from 'react';
import { useQuery } from 'react-query';

import { DialogInOne } from '../../../components';
import { Box } from '../../../components/MuiGenerals';
import { useAlerting, usePermission, useTranslation } from '../../../hooks';
import useLoading from '../../../hooks/useLoading';
import { ApproveHistory } from '../types/ApproveHistory';
import ActionButtons from './ActionButtons';
import ApprovalProgressDialogContent, { EnumApprovalStatus } from './ApprovalProgressDialogContent';
import YubiKeyDialog from '../../dialogs/YubiKeyDialog';
import { DivideLine } from '../../../components/Layout';
import { EnumTransactionStatus } from '../../../enum/EnumTransactionStatus';
import { useWeb3 } from '../../../hooks/useWeb3';
import { signTypedData } from '../../../utils/web3Provider';

interface IDataProps {
  createdBy: string;
  operator: string;
  approvalsRequired: number;
  approvalsCount: number;
  approveHistory?: ApproveHistory[];
  id: string;
  signaturesCollected?: number;
  signatureRequired?: number;
  signatures?: string[];
  allowedSignerAddresses?: string[];
}

interface IActionsProps {
  [key: string]: {
    apiFunc?: (
      params: { id: string | number; remarks: string; otp?: string },
      config?: any
    ) => Promise<any>;
    signApiCall?: (payload: any) => Promise<any>;
    withYubiKey?: boolean;
    permission: string;
  };
}
interface IProps {
  title?: string;
  isOpen: boolean;
  showApproveHistory?: boolean;
  closeDialog: () => void;
  refreshTable: () => void;
  data: IDataProps;
  children?: ReactNode;
  actions: IActionsProps;
  getApproveHistory: ({ id }: { id: string }) => Promise<any>;
  tailorMadeAlertMessages?: {
    approveSuccessMessage?: string;
    lastApproveSuccessMessage?: string;
    rejectSuccessMessage?: string;
    approvedBeforeMessage?: string;
    creatorApprovingMessage?: string;
  };
  selectedRow: any;
  web3: ReturnType<typeof useWeb3>;
}

const ApproveRejectDialog = (props: IProps) => {
  const {
    isOpen,
    closeDialog,
    data,
    refreshTable,
    children,
    actions,
    getApproveHistory,
    tailorMadeAlertMessages,
    selectedRow,
    showApproveHistory = true,
    web3,
  } = props;
  const { createdBy, operator, approvalsRequired, approvalsCount, id } = data;

  const {
    approveSuccessMessage,
    lastApproveSuccessMessage,
    rejectSuccessMessage,
    approvedBeforeMessage,
    creatorApprovingMessage,
  } = tailorMadeAlertMessages || {};

  const [approveHistory, setApproveHistory] = useState<ApproveHistory[]>();

  const [remarksInput, setRemarksInput] = useState<string>('');

  const [isYubiKeyApproveDialogOpen, setIsYubiKeyApproveDialogOpen] = useState<boolean>(false);
  const [isYubiKeyRejectDialogOpen, setIsYubiKeyRejectDialogOpen] = useState<boolean>(false);
  const [yubiKey, setYubiKey] = useState<string>('');

  const { tc } = useTranslation();
  const { alerting } = useAlerting();
  const { hasPermission } = usePermission();
  const { showLoading, hideLoading } = useLoading();
  const allowedSignerAddresses = data.allowedSignerAddresses || [];
  const { account } = web3;

  const getFirstApprover = () => {
    if (!approveHistory || approveHistory?.length === 0) return null;

    const sortedApprovedHistory = approveHistory.filter((item) => {
      return item.status === EnumApprovalStatus.Approved;
    });
    // .sort((a, b) => a.seq - b.seq);

    return sortedApprovedHistory?.[0]?.approvedBy;
  };

  const isLastApprover = approvalsRequired - approvalsCount === 1;
  const isSameOperator = createdBy === operator;
  const firstApprover = getFirstApprover();
  const isSameApprover = firstApprover === operator;
  const isShowApproveHistory = (approveHistory?.length || 0) > 0;

  const hasApprovePermission = hasPermission(actions.approve.permission);
  const hasRejectPermission = hasPermission(actions.reject.permission);

  const isRowPending =
    selectedRow && Number(selectedRow?.rawData?.status) === EnumTransactionStatus.Pending;

  const handleCloseDialog = () => {
    closeDialog();
    setRemarksInput('');
  };

  const closeDialogAndRefresh = () => {
    handleCloseDialog();
    refreshTable();
  };

  const checkIsCanApproveProceed = () => {
    if (!hasApprovePermission) {
      alerting('error', tc('no_relevant_permission'));
      return false;
    }

    if (isSameOperator) {
      const displayCreatorApprovingMessage =
        creatorApprovingMessage || tc('no_same_operator_creator');

      alerting('error', displayCreatorApprovingMessage);
      return false;
    }

    if (isSameApprover) {
      const displayApprovedBeforeMessage = approvedBeforeMessage || tc('no_same_approver');

      alerting('error', displayApprovedBeforeMessage);
      return false;
    }

    return true;
  };

  const selectApprovalLevel = () => {
    if (!approveHistory || approveHistory?.length === 0) return null;

    const sortedPendingApprovals = approveHistory.filter(
      (item) => item.status === EnumApprovalStatus.Pending
    );
    // .sort((a, b) => {
    //   return a.seq - b.seq;
    // });

    return sortedPendingApprovals[0];
  };

  const approveSuccessHandler = () => {
    const displayLastApproveSuccessMessage = lastApproveSuccessMessage || tc('request_approved');
    const displayApproveSuccessMessage = approveSuccessMessage || tc('approve_successful');

    const msg = isLastApprover ? displayLastApproveSuccessMessage : displayApproveSuccessMessage;

    setYubiKey('');
    alerting('success', msg);
    setIsYubiKeyApproveDialogOpen(false);
    closeDialogAndRefresh();
  };

  const rejectSuccessHandler = () => {
    const displayRejectSuccessMessage = rejectSuccessMessage || tc('request_rejected');

    setYubiKey('');
    alerting('success', displayRejectSuccessMessage);
    setIsYubiKeyRejectDialogOpen(false);
    closeDialogAndRefresh();
  };

  const handleApproveClick = async () => {
    // Check permission of that approval level
    // if (!hasPermission(approvalLevel.approvalPermission)) {
    //   return alerting('error', tc('no_relevant_permission'));
    // }

    if (actions.approve.withYubiKey) {
      // YubiKey flow
      return setIsYubiKeyApproveDialogOpen(true);
    }

    if (actions.approve.signApiCall) {
      // Sign approve flow
      const payload = {
        id: selectedRow?.rawData?.id,
        signatures: data.signatures,
        signerAddress: account || '',
      };
      console.log('[Sign API Call] : ', { account, payload, allowedSignerAddresses });

      if (!payload.signerAddress) {
        return alerting('error', 'Available MetaMask account not found. Please retry.');
      }

      const res = await actions.approve.signApiCall({
        id: selectedRow?.rawData?.id,
        signatures: data.signatures,
        signerAddress: account || '',
      });

      if (!res) return;

      approveSuccessHandler();
      return;
    }

    if (actions.approve.apiFunc) {
      // Validation
      const isCanProceed = checkIsCanApproveProceed();

      if (!isCanProceed) return;

      const approvalLevel = selectApprovalLevel();

      if (!approvalLevel) {
        return console.error('No approval level found');
      }

      // Approve
      showLoading('approveAction');
      const res = await actions.approve.apiFunc({
        id: approvalLevel.id,
        remarks: remarksInput,
      });
      hideLoading('approveAction');

      if (!res) return;

      approveSuccessHandler();
    }
  };

  const handleYubiKeyDialogConfirmApprove = async () => {
    const approvalLevel = selectApprovalLevel();

    if (!approvalLevel) {
      return console.error('No approval level found');
    }

    if (!yubiKey) {
      return alerting('error', tc('yubiKey_required'));
    }

    if (actions.approve.apiFunc) {
      showLoading('approveWithYubiKeyAction');
      const res = await actions.approve.apiFunc({
        id: approvalLevel.id,
        remarks: remarksInput,
        otp: yubiKey,
      });
      hideLoading('approveWithYubiKeyAction');

      if (!res) return;

      approveSuccessHandler();
    }
  };

  const handleRejectClick = async () => {
    if (!hasRejectPermission) {
      return alerting('error', tc('no_relevant_permission'));
    }

    const approvalLevel = selectApprovalLevel();

    if (!approvalLevel) return;

    if (actions.reject.withYubiKey) {
      return setIsYubiKeyRejectDialogOpen(true);
    }

    if (actions.reject.apiFunc) {
      showLoading('rejectAction');
      const res = await actions.reject.apiFunc({
        id: approvalLevel.id,
        remarks: remarksInput,
      });
      hideLoading('rejectAction');

      if (!res) return;

      rejectSuccessHandler();
    }
  };

  const handleYubiKeyDialogConfirmReject = async () => {
    const approvalLevel = selectApprovalLevel();

    if (!approvalLevel) {
      return console.error('No approval level found');
    }

    if (!yubiKey) {
      return alerting('error', tc('yubiKey_required'));
    }

    if (actions.reject.apiFunc) {
      showLoading('rejectWithYubiKey');
      const res = await actions.reject.apiFunc({
        id: approvalLevel.id,
        remarks: remarksInput,
        otp: yubiKey,
      });
      hideLoading('rejectWithYubiKey');

      if (!res) return;

      rejectSuccessHandler();
    }
  };

  const handleRemarksChange = (e: any) => {
    setRemarksInput(e.target.value);
  };

  const fetchApproveHistory = async () => {
    if (!id) return;

    showLoading('getApproveHistory');
    const res = getApproveHistory({ id });

    if (!res) return [];

    return res;
  };

  useQuery(`approveHistory-${id}`, fetchApproveHistory, {
    onSuccess: (data) => {
      setApproveHistory(data);
      hideLoading('getApproveHistory');
    },
  });

  const handleClose = () => {
    setYubiKey('');
    setIsYubiKeyApproveDialogOpen(false);
    setIsYubiKeyRejectDialogOpen(false);
  };

  const dialogConfig = {
    title: props.title || tc('approveReject'),
    self: {
      open: isOpen,
      onClose: () => {
        handleCloseDialog();
      },
    },
    onConfirm: () => {},
    onCancel: () => {
      handleCloseDialog();
    },
    isLoadingDialog: true,
    isConfirmHidden: true,
    size: 'xl' as any,
    isFullScreen: true,
    isCancelHidden: true,
    isShowCrossButton: true,
    content: (
      <div style={{ marginBottom: '24px' }}>
        {showApproveHistory && isShowApproveHistory && (
          <Box style={{}}>
            <h3>{tc('approvalProgressDialogTitle')}</h3>
            <ApprovalProgressDialogContent id={id} getApproveHistory={getApproveHistory} />
            <DivideLine verticalMargin="32px" />
          </Box>
        )}
        {children && <Box>{children}</Box>}
      </div>
    ),
    actionButtons: (
      <ActionButtons
        handleApproveClick={handleApproveClick}
        handleRejectClick={handleRejectClick}
        closeDialog={handleCloseDialog}
        remarksInput={remarksInput}
        handleRemarksChange={handleRemarksChange}
        customApproveBtnText={
          !isRowPending || !hasPermission(actions.approve.permission)
            ? undefined
            : tc('signApproveBtn', {
                signedAmount: data.signaturesCollected || 0,
                signedRequiredAmount: data.signatureRequired || 0,
              })
        }
        isApproveDisabled={
          !isRowPending ||
          !hasPermission(actions.approve.permission) ||
          Number(data.signaturesCollected) < Number(data.signatureRequired)
        }
        isRejectDisabled={!isRowPending || !hasPermission(actions.reject.permission)}
      />
    ),
  };

  if (!isOpen) return null;

  return (
    <>
      <DialogInOne {...dialogConfig}></DialogInOne>
      {/* YubiKey Approve Dialog */}
      <YubiKeyDialog
        isOpen={isYubiKeyApproveDialogOpen}
        onClose={handleClose}
        onConfirm={handleYubiKeyDialogConfirmApprove}
        onCancel={handleClose}
        yubiKey={yubiKey}
        setYubiKey={setYubiKey}
      />
      {/* YubiKey Reject Dialog */}
      <YubiKeyDialog
        isOpen={isYubiKeyRejectDialogOpen}
        onClose={handleClose}
        onConfirm={handleYubiKeyDialogConfirmReject}
        onCancel={handleClose}
        yubiKey={yubiKey}
        setYubiKey={setYubiKey}
      />
    </>
  );
};

export default ApproveRejectDialog;
